import React, { useCallback, useEffect, useRef, useState } from 'react';
import { searchStocks } from '../../../services/stockai/api/client';
import { formatKrw, normalizeSelectedStock } from '../../../services/stockai/stockUtils';

function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export default function StockSearchField({
  selected,
  onSelect,
  placeholder = '종목명 또는 종목코드 검색',
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const debouncedQuery = useDebouncedValue(query, 280);

  useEffect(() => {
    if (selected?.symbol) {
      setQuery(`${selected.name} (${selected.symbol})`);
    }
  }, [selected?.symbol, selected?.name]);

  useEffect(() => {
  const run = async () => {
      if (!debouncedQuery || debouncedQuery.length < 1) {
        setResults([]);
        return;
      }
      if (selected && debouncedQuery === `${selected.name} (${selected.symbol})`) {
        return;
      }
      setLoading(true);
      try {
        const data = await searchStocks(debouncedQuery);
        setResults(data.results || []);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [debouncedQuery, selected]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handlePick = useCallback((item) => {
    const stock = normalizeSelectedStock(item, 'search');
    onSelect?.(stock);
    setQuery(`${stock.name} (${stock.symbol})`);
    setOpen(false);
  }, [onSelect]);

  return (
    <div className="stockai-search" ref={wrapRef}>
      <label className="stockai-form-label">종목 검색</label>
      <input
        className="stockai-input stockai-search__input"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {loading && <div className="stockai-search__hint">검색 중...</div>}
      {open && results.length > 0 && (
        <ul className="stockai-search__dropdown">
          {results.map((item) => (
            <li key={item.symbol}>
              <button type="button" className="stockai-search__option" onClick={() => handlePick(item)}>
                <span>
                  <strong>{item.name}</strong>
                  <span className="stockai-stat-label"> {item.symbol}</span>
                </span>
                {item.price > 0 && (
                  <span className="stockai-stat-label">{formatKrw(item.price)}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && !loading && debouncedQuery && results.length === 0 && (
        <div className="stockai-search__empty">검색 결과가 없습니다.</div>
      )}
    </div>
  );
}
